import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { VideoFeatures } from '@/store/videoFeatures';
import { VideoElement } from './VideoElement';
import { JourneyPreviewOverlay } from './JourneyPreviewOverlay';
import { VideoControls } from './VideoControls';
import { AutoplayResizeWrapper } from './AutoplayResizeWrapper';
import { ContinueWatchingOverlay } from './ContinueWatchingOverlay';
import { PlayPauseOverlay } from './PlayPauseOverlay';
import { FinalCountdownOverlay } from './FinalCountdownOverlay';
import { ProgressBar } from './ProgressBar';
import { ExternalProgressBar } from './ui/ExternalProgressBar';
import { ActionButtonBelow } from './ui/ActionButtonBelow';
import { useVideoPlayerState } from './useVideoPlayerState';
import { cn } from '@/lib/utils';
import { useVideoFormTimer } from '@/hooks/use-video-form-timer';
// OCULTO TEMPORARIAMENTE - import { useSyncActions } from '@/hooks/use-sync-actions';
import { FormCaptureFields, PasswordCaptureFields } from '@/components/vsleads/types';
import { ThumbnailOverlays } from './ThumbnailOverlays';
import { VSLeadsPasswordOverlay } from '@/components/vsleads/VSLeadsPasswordOverlay';
import { VideoFeatureOverlays } from './VideoFeatureOverlays';
import { FormOverlay } from './FormOverlay';
import { SessionResumeModal } from './SessionResumeModal';
import { sanitizeHTML } from '@/lib/security';
import { FakeProgressBar } from './FakeProgressBar';
import { authApi } from '@/services/authService';
import { SubtitleTrack } from '@/store/videoFeatures/types/video-features';
import { selectVisibleActionButtons } from '@/lib/action-button/list';
import { useActionButtonEditorStore } from '@/store/videoFeatures/actionButtonEditorStore';
// OCULTO TEMPORARIAMENTE - import { useDeviceDetection } from '@/hooks/use-device-detection';
// OCULTO TEMPORARIAMENTE - import { resolvePlayerContent } from '@/lib/utils/responsive-player-resolver';
// OCULTO TEMPORARIAMENTE - import { ImageReplacementView } from './ImageReplacementView';

interface VideoPlayerProps {
  features: VideoFeatures;
  formConfig?: FormCaptureFields;
  passwordConfig?: PasswordCaptureFields;
  // Overlay de preview da Jornada de Conversão cobre o vídeo principal. Só deve
  // aparecer quando o admin está editando a aba "Jornada de Conversão"; em
  // qualquer outro contexto (demais abas, VSLeads, uploads, stage) mostramos o
  // vídeo principal. Por isso o default é false.
  showJourneyPreview?: boolean;
  // O bloco de oferta "abaixo do vídeo" fica em fluxo e pode ser alto (headline,
  // preço, barra de progresso, dois botões). Só é renderizado no painel do
  // próprio botão de ação, onde há scroll para ele; nas demais telas ocuparia o
  // espaço do player sem que ninguém esteja editando aquilo. Também ignora a
  // janela de tempo — quem configura "aparece aos 30s" precisa vê-lo enquanto edita.
  showActionButtonPreview?: boolean;
  // Id do painel aberto no editor (vem do LivePreviewPanel). Serve de gate para
  // os overlays que o editor precisa mostrar FORÇADOS enquanto se edita aquele
  // recurso — hoje só a Notificação de Vendas — sem que eles vazem para o
  // preview dos demais painéis. Em produção não é passado (undefined).
  editingPanel?: string | null;
  // true quando este player é preview do dashboard (editor, upload, VSLeads).
  // Desarma a navegação real da Contagem Final — sem isso, deixar o vídeo do
  // preview terminar leva o dashboard inteiro para a URL de redirecionamento e
  // o usuário perde as edições não salvas. Mesmo conceito do `isPreviewMode`
  // que o useContinueWatching já expõe.
  isPreviewMode?: boolean;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ features, formConfig, passwordConfig, showJourneyPreview = false, showActionButtonPreview = false, editingPanel = null, isPreviewMode: isEditorPreview = false }) => {
  useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Aplica/remove a classe `is-fullscreen` no container quando o usuário entra
  // ou sai do fullscreen. Necessário porque o seletor :fullscreen pode falhar
  // silenciosamente em algumas combinações de navegador (Safari/Mac sem
  // prefixo, regras combinadas com vírgula, etc). A classe garante que o CSS
  // que força width/height 100% e centralização sempre seja aplicado.
  useEffect(() => {
    const handleFsChange = () => {
      const fsEl =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement;
      const container = containerRef.current;
      if (!container) return;
      if (fsEl === container) {
        container.classList.add('is-fullscreen');
      } else {
        container.classList.remove('is-fullscreen');
      }
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    document.addEventListener('webkitfullscreenchange', handleFsChange);
    document.addEventListener('mozfullscreenchange', handleFsChange);
    document.addEventListener('MSFullscreenChange', handleFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFsChange);
      document.removeEventListener('webkitfullscreenchange', handleFsChange);
      document.removeEventListener('mozfullscreenchange', handleFsChange);
      document.removeEventListener('MSFullscreenChange', handleFsChange);
    };
  }, []);

  const {
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isVertical,
    showAutoplayOverlay,
    showControls,
    showContinueWatchingOverlay,
    showInitialThumbnail,
    showPauseThumbnail,
    showEndThumbnail,
    handleContinue,
    handleRestart,
    togglePlay,
    handleTimeUpdate,
    handleDurationChange,
    handlePlay,
    handlePause,
    handleVolumeChange,
    handleSkipForward,
    handleSkipBack,
    toggleMute,
    handleVolumeSliderChange,
    handleSeek,
    toggleFullscreen,
    handleAutoplayOverlayClick,
    handleThumbnailClick,
    formatTime,
    setShowControls,
    autoplayOverlayEnabled,
    handleVideoEnded,
    togglePictureInPicture,
    setPlaybackSpeed,
    showSessionModal,
    sessionPosition,
    handleSessionResume,
    handleSessionRestart,
    handleSessionCancel,
    showFinalCountdown,
    countdownTime,
    handleCountdownRedirect,
    handleSkipCountdown,
    handleDismissCountdown,
    countdownCanGo,
    countdownHasRedirected,
    isPreviewMode,
    showPlayIcon,
    handlePlayIconClick
  } = useVideoPlayerState(videoRef, containerRef, features, isEditorPreview);

  // Abre o seletor nativo de TV (AirPlay no Safari, Chromecast/Remote
  // Playback no Chrome/Edge). Dá feedback explícito via toast pra qualquer
  // cenário (sucesso, cancel, sem receiver, erro).
  const handleTvCast = React.useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vAny = video as any;

    // Safari (iOS/macOS) → AirPlay
    if (typeof vAny.webkitShowPlaybackTargetPicker === 'function') {
      try {
        vAny.webkitShowPlaybackTargetPicker();
        return;
      } catch (err) {
        console.warn('[TvCast] AirPlay picker falhou:', err);
        toast.error(translateInterfaceText("Não foi possível abrir o seletor AirPlay"));
        return;
      }
    }

    // Chrome/Edge → Remote Playback API (inclui Chromecast).
    // Quando hls.js está ativo, o <video> usa MediaSource e o Chrome
    // dispara NotAllowedError em `remote.prompt()` (a API não consegue
    // serializar conteúdo MSE pro Chromecast). Workaround: trocar o src
    // pela URL HLS direta — Chromecast reproduz HLS nativo. Se o usuário
    // cancelar, restauramos o hls.js.
    if (vAny.remote && typeof vAny.remote.prompt === 'function') {
      const hls = vAny._hlsInstance;
      const directUrl = features.videoStreamUrl || features.videoUrl;
      const useSwap = !!hls && !!directUrl;
      const savedTime = video.currentTime || 0;
      const wasPlaying = !video.paused;

      if (useSwap) {
        try { hls.detachMedia(); } catch {}
        try {
          video.src = directUrl as string;
          video.load();
          if (savedTime > 0) {
            try { video.currentTime = savedTime; } catch {}
          }
        } catch {}
      }

      const restoreHls = () => {
        if (!useSwap) return;
        try {
          video.removeAttribute('src');
          while (video.firstChild) video.removeChild(video.firstChild);
          video.load();
          hls.attachMedia(video);
          const seek = () => {
            try { video.currentTime = savedTime; } catch {}
            if (wasPlaying) {
              const p = video.play();
              if (p && typeof p.catch === 'function') p.catch(() => {});
            }
          };
          video.addEventListener('loadedmetadata', seek, { once: true });
        } catch {}
      };

      try {
        await vAny.remote.prompt();
        // Sucesso — o cast assume. Mantém o src direto.
        return;
      } catch (err) {
        const name = (err as { name?: string })?.name || '';
        console.warn('[TvCast] Remote Playback falhou:', name, err);
        restoreHls();
        if (name === 'NotFoundError') {
          toast.error(
            translateInterfaceText("Nenhum dispositivo encontrado"),
            { description: translateInterfaceText("Verifique se sua Chromecast/Smart TV está ligada e conectada na mesma rede Wi-Fi.") }
          );
        } else if (name === 'NotAllowedError') {
          toast.error(
            translateInterfaceText("Transmissão indisponível"),
            { description: translateInterfaceText("Esse vídeo não pode ser transmitido a partir deste navegador.") }
          );
        } else if (name === 'AbortError') {
          // user cancelou — silencioso
        } else {
          toast.error(translateInterfaceText("Não foi possível abrir a transmissão"));
        }
        return;
      }
    }

    // Fallback: browser sem nenhuma das APIs (raro porque o botão já filtra)
    toast.error(
      translateInterfaceText("Transmissão não suportada neste navegador"),
      { description: translateInterfaceText("Tente abrir o vídeo no Chrome (Chromecast) ou Safari (AirPlay).") }
    );
  }, [videoRef, features.videoStreamUrl, features.videoUrl]);

  // ===================== SUBTITLE STATE =====================
  const subtitles = (features.subtitles || []) as SubtitleTrack[];
  const subtitleConfig = features.subtitleConfig;

  const [subtitleVisible, setSubtitleVisible] = React.useState(subtitleConfig?.showByDefault ?? false);
  const [activeSrclang, setActiveSrclang] = React.useState<string | null>(() => {
    const def = subtitles.find(s => s.isDefault) || subtitles[0];
    return def?.srclang ?? null;
  });
  const [parsedCues, setParsedCues] = React.useState<Record<string, Array<{start: number, end: number, text: string}>>>({});

  React.useEffect(() => {
    setSubtitleVisible(subtitleConfig?.showByDefault ?? false);
  }, [subtitleConfig?.showByDefault]);

  // Quando subtitles carrega do backend (assíncrono), atualiza activeSrclang se ainda não foi definido
  React.useEffect(() => {
    if (activeSrclang) return;
    const def = subtitles.find(s => s.isDefault) || subtitles[0];
    if (def) setActiveSrclang(def.srclang);
  }, [subtitles]);

  React.useEffect(() => {
    if (!subtitles.length) return;

    const parseTime = (str: string) => {
      const parts = str.trim().split(':');
      if (parts.length === 3) return parseFloat(parts[0]) * 3600 + parseFloat(parts[1]) * 60 + parseFloat(parts[2]);
      return parseFloat(parts[0]) * 60 + parseFloat(parts[1]);
    };

    const parseVtt = (text: string) => {
      const cues: Array<{start: number, end: number, text: string}> = [];
      const lines = text.replace(/\r\n/g, '\n').split('\n');
      let i = 0;
      while (i < lines.length) {
        if (lines[i].includes('-->')) {
          const [startRaw, endRaw] = lines[i].split('-->').map(s => s.trim().split(' ')[0]);
          const start = parseTime(startRaw);
          const end = parseTime(endRaw);
          const textLines: string[] = [];
          i++;
          while (i < lines.length && lines[i].trim() !== '') {
            textLines.push(lines[i].replace(/<[^>]+>/g, '').trim());
            i++;
          }
          if (textLines.length) cues.push({ start, end, text: textLines.join('<br>') });
        }
        i++;
      }
      return cues;
    };

    const fetchText = async (url: string): Promise<string> => {
      try {
        const pathname = new URL(url).pathname;
        if (pathname.includes('/videos/subtitle/')) {
          const res = await authApi.get(pathname, { responseType: 'text' });
          return res.data;
        }
        const res = await fetch(url);
        if (!res.ok) {
          console.warn('[Subtitle] Erro ao buscar arquivo:', url, res.status);
          return '';
        }
        return res.text();
      } catch (err) {
        console.warn('[Subtitle] Falha ao buscar arquivo:', url, err);
        return '';
      }
    };

    subtitles.forEach(async (sub) => {
      const text = await fetchText(sub.url);
      if (!text) return;
      const vtt = text.trimStart().startsWith('WEBVTT') ? text : 'WEBVTT\n\n' + text.replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2');
      const cues = parseVtt(vtt);
      setParsedCues(prev => ({ ...prev, [sub.srclang]: cues }));
    });
  }, [subtitles]);

  const handleToggleSubtitle = () => setSubtitleVisible(v => !v);
  const handleSelectSubtitle = (srclang: string) => {
    setActiveSrclang(srclang);
    setSubtitleVisible(true);
  };

  const activeCues = activeSrclang ? (parsedCues[activeSrclang] || []) : [];
  const currentSubtitleCue = activeCues.find(c => currentTime >= c.start && currentTime < c.end) || null;
  // ===================== END SUBTITLE STATE =====================

  // OCULTO TEMPORARIAMENTE - Responsive Player Detection
  // const currentDevice = useDeviceDetection();
  // const resolvedContent = resolvePlayerContent(
  //   features.responsivePlayer,
  //   currentDevice,
  //   features.videoUrl
  // );

  // OCULTO TEMPORARIAMENTE - Sync Actions - Detect preview mode
  // const isPreview = typeof window !== 'undefined' && (
  //   window.location.pathname.startsWith('/new-video') ||
  //   window.location.pathname.startsWith('/personalizations')
  // );
  // useSyncActions(videoRef, features.syncActions, isPreview);

  // Use the form timer hook if formConfig is provided
  const { showForm, handleFormSubmit } = useVideoFormTimer(
    videoRef,
    formConfig && formConfig.timerEnabled ? formConfig : undefined
  );

  // Password overlay state
  const [showPasswordOverlay, setShowPasswordOverlay] = React.useState(false);
  const [passwordUnlocked, setPasswordUnlocked] = React.useState(false);

  // Handle password timer
  React.useEffect(() => {
    if (!passwordConfig || !passwordConfig.timer.enabled || passwordUnlocked) return;

    const handlePasswordTimer = () => {
      const video = videoRef.current;
      if (!video) return;

      const [minutes, seconds] = passwordConfig.timer.showAt.split(':').map(Number);
      const targetSeconds = (minutes || 0) * 60 + (seconds || 0);
      const currentSeconds = Math.floor(video.currentTime);

      if (currentSeconds >= targetSeconds && !showPasswordOverlay) {
        if (passwordConfig.timer.pauseOnOpen) {
          video.pause();
        }
        setShowPasswordOverlay(true);
      }
    };

    const video = videoRef.current;
    if (video) {
      video.addEventListener('timeupdate', handlePasswordTimer);
      return () => video.removeEventListener('timeupdate', handlePasswordTimer);
    }
  }, [passwordConfig, showPasswordOverlay, passwordUnlocked, videoRef]);

  const handlePasswordSubmit = (password: string) => {
    setPasswordUnlocked(true);
    setShowPasswordOverlay(false);
    
    const video = videoRef.current;
    if (video && !passwordConfig?.timer.pauseOnSubmit) {
      video.play().catch(err => console.error('Error playing video:', err));
    } else if (video && passwordConfig?.timer.pauseOnSubmit) {
      video.pause();
    }
  };

  // Calculate border and shadow styles with new properties
  const getBorderStyles = () => {
    const styles: React.CSSProperties = {};
    
    // Handle border properties (new interface priority, fallback to legacy)
    const borderEnabled = features.border?.enabled ?? features.enableBorder;
    if (borderEnabled) {
      const borderWidth = features.border?.width ?? features.borderWidth ?? 2;
      const borderColor = features.border?.color ?? features.borderColor ?? '#ffffff';
      const borderRadius = features.border?.radius ?? features.borderRadius ?? 0;
      
      styles.borderWidth = `${borderWidth}px`;
      styles.borderStyle = 'solid';
      styles.borderColor = borderColor;
      styles.borderRadius = `${borderRadius}px`;
    }
    
    // Apply shadow if enabled
    if (features.border?.shadow?.enabled) {
      const shadowColor = features.border.shadow.color;
      const shadowBlur = features.border.shadow.blur;
      const shadowIntensity = features.border.shadow.intensity;
      
      // Parse RGBA and apply intensity
      const match = shadowColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([0-9.]+))?\)/);
      if (match) {
        const r = match[1];
        const g = match[2];
        const b = match[3];
        const a = shadowIntensity / 100;
        styles.boxShadow = `0 0 ${shadowBlur}px rgba(${r}, ${g}, ${b}, ${a})`;
      } else if (shadowColor.startsWith('#')) {
        // Convert HEX to RGBA with intensity
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(shadowColor);
        if (result) {
          const r = parseInt(result[1], 16);
          const g = parseInt(result[2], 16);
          const b = parseInt(result[3], 16);
          const a = shadowIntensity / 100;
          styles.boxShadow = `0 0 ${shadowBlur}px rgba(${r}, ${g}, ${b}, ${a})`;
        }
      }
    }
    
    return styles;
  };
  

  // Helper function to convert mm:ss to seconds
  const timeToSeconds = (timeStr?: string): number => {
    if (!timeStr) return 0;
    const [minutes, seconds] = timeStr.split(':').map(num => parseInt(num, 10));
    return (minutes || 0) * 60 + (seconds || 0);
  };

  // Show headline based on current time (same logic as action button)
  const shouldShowHeadline = features.headline && features.headline.enabled && (
    features.headline.timing?.showThroughout === true ||
    (features.headline.timing?.showThroughout === false &&
     currentTime >= timeToSeconds(features.headline.timing?.start) &&
     (features.headline.timing?.end ? currentTime <= timeToSeconds(features.headline.timing?.end) : true)) ||
    (!features.headline.timing?.hasOwnProperty('showThroughout') &&
     !features.headline.timing?.start && !features.headline.timing?.end)
  );

  // Botões de ação ligados que aparecem agora. Com o painel do botão aberto, o botão aberto no
  // editor aparece sempre, para o usuário ver o que está editando.
  const openActionButtonId = useActionButtonEditorStore((state) => state.openButtonId);
  const visibleActionButtons = selectVisibleActionButtons(
    features.actionButtons,
    currentTime,
    showActionButtonPreview ? openActionButtonId : null,
  );
  const overActionButtons = visibleActionButtons.filter((b) => b.buttonType === 'sobre-video');
  const belowActionButtons = visibleActionButtons.filter((b) => (b.buttonType || 'abaixo-video') === 'abaixo-video');
  // `??` e não `||`: apagar o código (html === '') esvazia a prévia na hora.
  const htmlActionButtons = visibleActionButtons.filter(
    (b) => b.buttonType === 'html' && (b.html ?? b.customHtml ?? '') !== '',
  );

  // Determine if we should use custom controls based on autoplay overlay status and form status
  const useCustomControls = features.showControls && !features.disableControls && 
    !(autoplayOverlayEnabled && showAutoplayOverlay) && !showForm && 
    !showInitialThumbnail && !showPauseThumbnail && !showEndThumbnail;


  // OCULTO TEMPORARIAMENTE - Handle Responsive Player Logic
  // if (!resolvedContent.shouldRender) {
  //   if (resolvedContent.imageReplacement) {
  //     return (
  //       <div className="relative">
  //         <div
  //           className={cn(
  //             "relative w-full aspect-video bg-black rounded-lg overflow-hidden",
  //             features.largePlayer ? "max-w-full" : "max-w-3xl mx-auto"
  //           )}
  //           style={getBorderStyles()}
  //         >
  //           <ImageReplacementView replacement={resolvedContent.imageReplacement} />
  //         </div>
  //       </div>
  //     );
  //   }
  //   return null;
  // }

  // OCULTO TEMPORARIAMENTE - const videoUrlToUse = resolvedContent.videoUrl || features.videoUrl;
  const videoUrlToUse = features.videoUrl;

  // Calculate inner border radius to compensate for border width
  const outerBorderRadius = features.border?.radius ?? features.borderRadius ?? 0;
  const borderWidth = features.border?.width ?? features.borderWidth ?? 0;
  const borderEnabled = features.border?.enabled ?? features.enableBorder ?? false;

  // Inner radius = outer radius - border width (to fit perfectly inside)
  // Subtract slightly more to prevent overflow beyond the border.
  // Sem borda ativa não arredonda (espelha o editor: desativar a borda zera o radius).
  const innerBorderRadius = borderEnabled && outerBorderRadius > 0
    ? Math.max(0, outerBorderRadius - borderWidth - 1)
    : 0;

  return (
    <div className="relative" data-theme-isolated>
      {/* Border wrapper - borda por fora */}
      <div
        className={cn(
          "relative flex flex-col",
          features.largePlayer ? "w-full" : "items-start mx-auto"
        )}
        style={{
          ...getBorderStyles(),
          ...(!features.largePlayer && { width: 'fit-content', maxWidth: '100%' })
        }}
      >
        {/* Video container - se ajusta ao tamanho natural do vídeo */}
        <div
          className={cn(
            "relative bg-black overflow-hidden",
            features.disableControls ? "pointer-events-none cursor-default" : ""
          )}
          ref={containerRef}
          data-video-container
          style={{
            ...(innerBorderRadius > 0 && { borderRadius: `${innerBorderRadius}px` })
          }}
        >
        <VideoElement
          videoRef={videoRef}
          features={{ ...features, videoUrl: videoUrlToUse }}
          togglePlay={togglePlay}
          useCustomControls={useCustomControls}
          isMuted={isMuted}
          showAutoplayOverlay={showAutoplayOverlay}
          autoplayOverlayEnabled={autoplayOverlayEnabled}
          onTimeUpdate={handleTimeUpdate}
          onDurationChange={handleDurationChange}
          onPlay={handlePlay}
          onPause={handlePause}
          onVolumeChange={handleVolumeChange}
          onEnded={handleVideoEnded}
          onAutoplayOverlayClick={handleAutoplayOverlayClick}
        />

        {/* Jornada de Conversão — overlay de preview (entrada/saída) cobrindo
            exatamente a caixa do vídeo principal, sem alterar suas dimensões.
            Só renderiza na aba "Jornada de Conversão" (showJourneyPreview);
            fora dela mostramos o vídeo principal. */}
        {showJourneyPreview && features.conversionJourney?.enabled && (
          <JourneyPreviewOverlay journey={features.conversionJourney} />
        )}

        {/* Subtitle Overlay */}
        {subtitleVisible && currentSubtitleCue && subtitleConfig && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              zIndex: 15,
              pointerEvents: 'none',
              textAlign: (subtitleConfig.textAlign as any) || 'center',
              padding: '0 12px',
              ...(subtitleConfig.position === 'top'
                ? { top: '5%' }
                : subtitleConfig.position === 'middle'
                ? { top: '50%', transform: 'translateY(-50%)' }
                : { bottom: '10%' }),
            }}
          >
            <span
              style={{
                display: 'inline-block',
                background: subtitleConfig.backgroundColor || 'rgba(0,0,0,0.75)',
                color: subtitleConfig.fontColor || '#FFFFFF',
                fontSize: subtitleConfig.fontSize || '1em',
                fontFamily: subtitleConfig.fontFamily || 'inherit',
                padding: '2px 8px',
                borderRadius: '2px',
                maxWidth: '90%',
                textShadow: subtitleConfig.textShadow ? '1px 1px 3px rgba(0,0,0,0.9)' : 'none',
              }}
              dangerouslySetInnerHTML={{ __html: currentSubtitleCue.text }}
            />
          </div>
        )}

        {/* Thumbnail Overlays */}
        <ThumbnailOverlays
          features={features}
          showInitialThumbnail={showInitialThumbnail}
          showPauseThumbnail={showPauseThumbnail}
          showEndThumbnail={showEndThumbnail}
          onThumbnailClick={handleThumbnailClick}
        />

        {/* Autoplay overlay — com wrapper de redimensionamento livre.
            Oculto quando o "continuar de onde parou" está visível: esse overlay
            tem prioridade e os dois não podem aparecer sobrepostos. */}
        {autoplayOverlayEnabled && showAutoplayOverlay && features.autoplayOverlay && !showContinueWatchingOverlay && (
          <AutoplayResizeWrapper
            features={features}
            onClick={handleAutoplayOverlayClick}
            containerRef={containerRef}
          />
        )}

        {/* Play icon overlay - shown when video is paused in these scenarios:
            1. After autoplay interaction (user clicked autoplay overlay)
            2. When controls are disabled/hidden and autoplay is not active */}
        {showPlayIcon && (
          <PlayPauseOverlay
            isVisible={showPlayIcon}
            onClick={handlePlayIconClick}
            showFullscreenButton={!!features.autoplayOverlay?.haveFullScreen}
            onFullscreenClick={toggleFullscreen}
          />
        )}

        {/* Continue watching overlay */}
        {showContinueWatchingOverlay && (
          <ContinueWatchingOverlay
            features={features}
            onContinue={handleContinue}
            onRestart={handleRestart}
          />
        )}

        {/* Session Resume Modal - DESABILITADO - Usar apenas ContinueWatchingOverlay */}
        {/*
        {!isPreviewMode && (
          <SessionResumeModal
            isVisible={showSessionModal}
            position={sessionPosition || 0}
            onResume={handleSessionResume}
            onRestart={handleSessionRestart}
            onCancel={handleSessionCancel}
          />
        )}
        */}

        {/* VSLeads Timer Form Overlay */}
        <FormOverlay
          formConfig={formConfig}
          showForm={showForm}
          handleFormSubmit={handleFormSubmit}
        />

        {/* VSLeads Password Overlay */}
        {passwordConfig && (
          <VSLeadsPasswordOverlay
            config={passwordConfig}
            onSubmit={handlePasswordSubmit}
            isVisible={showPasswordOverlay}
          />
        )}

        {/* Custom controls */}
        {useCustomControls && (
          <VideoControls
            features={features}
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            volume={volume}
            isMuted={isMuted}
            isVertical={isVertical}
            showControls={showControls}
            togglePlay={togglePlay}
            handleSkipBack={handleSkipBack}
            handleSkipForward={handleSkipForward}
            toggleMute={toggleMute}
            handleVolumeChange={handleVolumeSliderChange}
            handleSeek={handleSeek}
            toggleFullscreen={toggleFullscreen}
            togglePictureInPicture={togglePictureInPicture}
            onTvCast={handleTvCast}
            setPlaybackSpeed={setPlaybackSpeed}
            formatTime={formatTime}
            subtitles={subtitles}
            subtitleVisible={subtitleVisible}
            activeSrclang={activeSrclang}
            onToggleSubtitle={handleToggleSubtitle}
            onSelectSubtitle={handleSelectSubtitle}
          />
        )}

        {/* Fake Progress Bar (haveFakeBar) - ativo quando o recurso está habilitado,
            única restrição: não pode coexistir com haveControls ativo */}
        {features.progressBar?.enabled &&
         !features.showControls && (
          <FakeProgressBar
            videoRef={videoRef}
            currentTime={currentTime}
            duration={duration}
            color={features.progressBarColor || features.playerColors?.primary || features.controlsColor || '#3b82f6'}
            height={features.progressBar?.height || features.progressBarHeight || 12}
            hasBorder={false}
            borderRadius={0}
            borderWidth={0}
          />
        )}

        {/* Feature Overlays (logo, headline, action button, viewer count, etc) */}
        <VideoFeatureOverlays
          features={features}
          currentTime={currentTime}
          shouldShowHeadline={shouldShowHeadline}
          overActionButtons={overActionButtons}
          isPlaying={isPlaying}
          autoplayOverlayEnabled={autoplayOverlayEnabled}
          showAutoplayOverlay={showAutoplayOverlay}
          togglePlay={togglePlay}
          onSkipBack={handleSkipBack}
          onSkipForward={handleSkipForward}
          isVideoEnded={false}
          editingPanel={editingPanel}
        />

        </div>
      </div>

      {/* Botões de ação abaixo do vídeo — em fluxo, fora da caixa do vídeo, um embaixo do outro.
          É aqui que o bloco de oferta (os 8 modelos) aparece. */}
      {belowActionButtons.map((button) => (
        <div key={button.id} className="w-full mt-4">
          <ActionButtonBelow actionButton={button} />
        </div>
      ))}

      {/* Botões de ação com HTML personalizado — abaixo do vídeo, em fluxo, como no player. */}
      {htmlActionButtons.map((button) => (
        <div
          key={button.id}
          className="w-full flex justify-center mt-4 cursor-pointer"
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(button.html ?? button.customHtml ?? '') }}
          onClick={() =>
            button.url && window.open(button.url, button.template?.openInNewTab !== false ? '_blank' : '_self')
          }
        />
      ))}

      {/* Contagem Final Inteligente — aparece quando o vídeo termina */}
      {showFinalCountdown && features.finalCountdown && (
        <FinalCountdownOverlay
          config={features.finalCountdown}
          timeRemaining={countdownTime}
          onRedirect={handleCountdownRedirect}
          /* Botão "Ir Agora" só existe quando há para onde ir — mesma regra do
             player (`showButton && canGo`). */
          onSkip={features.finalCountdown.showButton && countdownCanGo ? handleSkipCountdown : undefined}
          hasRedirected={countdownHasRedirected}
          onDismiss={handleDismissCountdown}
        />
      )}
    </div>
  );
};
